import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Calendar, Coffee, Users, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { apiFetch } from '../../config/api'

export default function DashboardHome() {
  const [stats, setStats] = useState([
    { label: 'Aktive Events', value: '0', icon: Calendar, color: 'from-brand-600 to-brand-600', border: 'border-brand-500/30', iconColor: 'text-brand-400' },
    { label: 'Benutzer', value: '0', icon: Users, color: 'from-brand-600 to-brand-600', border: 'border-brand-500/30', iconColor: 'text-brand-400' },
  ])
  const [loading, setLoading] = useState(true)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [serverUptime, setServerUptime] = useState<string | null>(null)
  const [serverResponseMs, setServerResponseMs] = useState<number | null>(null)
  const [visitorsToday, setVisitorsToday] = useState<number | null>(null)
  const [pageViews7d, setPageViews7d] = useState<number | null>(null)
  const [serverOnline, setServerOnline] = useState(true)

  useEffect(() => {
    // Lade User-Info
    const userStr = localStorage.getItem('dashboard_user')
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr))
      } catch (e) {
        console.error('Error parsing user data:', e)
      }
    }
  }, [])

  const getAuthToken = () => {
    return localStorage.getItem('dashboard_token') || ''
  }

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Lade Benutzeranzahl
        let userCount = 0
        try {
          const usersResponse = await apiFetch('/users', {
            headers: {
              'Authorization': getAuthToken(),
            },
          })
          const usersData = await usersResponse.json()
          userCount = usersData.success && usersData.users ? usersData.users.length : 0
        } catch (e) {
          console.error('Error fetching users:', e)
        }

        // Lade Events
        let eventCount = 0
        try {
          const eventsResponse = await apiFetch('/events', {
            headers: {
              'Authorization': getAuthToken(),
            },
          })
          const eventsData = await eventsResponse.json()
          if (eventsData.success && eventsData.data) {
            // API gibt 'data' zurück, nicht 'events'
            eventCount = Array.isArray(eventsData.data) 
              ? eventsData.data.filter((e: any) => e.status === 'Aktiv').length 
              : 0
          }
        } catch (e) {
          console.error('Error fetching events:', e)
        }

        setStats([
          { label: 'Aktive Events', value: eventCount.toString(), icon: Calendar, color: 'from-brand-600 to-brand-600', border: 'border-brand-500/30', iconColor: 'text-brand-400' },
          { label: 'Benutzer', value: userCount.toString(), icon: Users, color: 'from-brand-600 to-brand-600', border: 'border-brand-500/30', iconColor: 'text-brand-400' },
        ])
      } catch (err) {
        console.error('Error fetching stats:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  useEffect(() => {
    const fetchServerAndAnalytics = async () => {
      const token = getAuthToken()
      if (!token) return
      try {
        const start = performance.now()
        const res = await apiFetch('/stats', { headers: { 'Authorization': token } })
        const ms = Math.round(performance.now() - start)
        setServerResponseMs(ms)
        if (!res.ok) {
          setServerOnline(false)
          return
        }
        const data = await res.json()
        if (data.success) {
          setServerOnline(data.server?.online ?? true)
          setServerUptime(data.server?.uptime ?? null)
          setVisitorsToday(data.visitors_today ?? 0)
          setPageViews7d(data.page_views_7d ?? 0)
        }
      } catch {
        setServerOnline(false)
      }
    }
    fetchServerAndAnalytics()
  }, [])

  return (
    <div>
      {/* Header with animated gradient and welcome message */}
      <motion.div 
        className="mb-6 sm:mb-8 md:mb-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
          <div className="flex-1 min-w-0">
            {/* Welcome Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex flex-wrap items-center gap-2 bg-gradient-to-r from-brand-600/20 to-brand-600/20 border border-brand-500/30 rounded-full px-3 py-2 sm:px-4 mb-3 sm:mb-4"
            >
              <Sparkles className="w-4 h-4 text-brand-400 shrink-0" />
              <span className="text-xs sm:text-sm font-medium text-brand-400 break-words">
                {new Date().getHours() < 12 ? 'Guten Morgen' : new Date().getHours() < 18 ? 'Guten Tag' : 'Guten Abend'}
                {currentUser && `, ${currentUser.name || 'Admin'}`}!
              </span>
            </motion.div>

            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-white mb-2 sm:mb-3 bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-brand-400 break-words">
              Dashboard Übersicht
            </h1>
            <p className="text-gray-300 text-base sm:text-lg mb-4">Hier ist deine Übersicht über alle wichtigen Bereiche.</p>
            <div className="h-1 w-32 bg-gradient-to-r from-brand-500 via-brand-500 to-brand-500 rounded-full shadow-lg shadow-brand-500/50" />
          </div>
        </div>
      </motion.div>

      {/* Stats Grid with enhanced design */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8 md:mb-10">
        {stats.map((stat, index) => {
          const Icon = stat.icon
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.5, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 100
              }}
              className="relative group cursor-pointer"
              whileHover={{ y: -8, scale: 1.02 }}
            >
              {/* Card Container */}
              <div className={`relative bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border ${stat.border} p-5 sm:p-6 md:p-7 overflow-hidden transition-all duration-500 group-hover:shadow-2xl group-hover:border-opacity-100`}>
                {/* Animated gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                
                {/* Glow effect on hover */}
                <div className={`absolute -inset-1 bg-gradient-to-r ${stat.color} rounded-2xl blur-lg opacity-0 group-hover:opacity-20 transition-opacity duration-500 -z-10`} />
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="flex items-center justify-between mb-5">
                    <motion.div 
                      className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-2xl ring-4 ring-white/5`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Icon className="h-7 w-7 text-white" />
                    </motion.div>
                    {/* Decorative element */}
                    <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${stat.color} opacity-20 blur-sm`} />
                  </div>
                  
                  {/* Value with counter animation */}
                  <motion.h3 
                    className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 sm:mb-3"
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    {stat.value}
                  </motion.h3>
                  
                  <p className="text-gray-300 text-sm font-medium tracking-wide uppercase">{stat.label}</p>
                  
                  {/* Animated accent line */}
                  <div className={`h-1 w-16 bg-gradient-to-r ${stat.color} mt-5 rounded-full group-hover:w-full transition-all duration-700 shadow-lg`} />
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Server Stats and Quick Access Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        {/* Server Statistics & Website Analytics */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="relative group min-w-0"
        >
          <div className="relative bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/10 p-5 sm:p-6 md:p-8 overflow-hidden transition-all duration-500 hover:shadow-2xl">
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-brand-500/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="h-8 sm:h-10 w-1 bg-gradient-to-b from-brand-500 to-brand-500 rounded-full shrink-0" />
                <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-light text-white break-words">
                  Server & Analytics
                </h2>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                {/* Server Status */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="p-4 sm:p-5 rounded-xl bg-gradient-to-br from-brand-600/10 to-brand-700/10 border border-brand-500/20"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full shadow-lg animate-pulse ${serverOnline ? 'bg-green-500 shadow-green-500/50' : 'bg-red-500 shadow-red-500/50'}`}></div>
                      <span className="text-white font-semibold">Server Status</span>
                    </div>
                    <span className={serverOnline ? 'text-brand-400 text-sm font-medium' : 'text-red-400 text-sm font-medium'}>
                      {serverOnline ? 'Online' : 'Offline'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-300">Uptime</span>
                      <p className="text-white font-medium">
                        {serverUptime ?? '—'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-300">Response</span>
                      <p className="text-white font-medium">
                        {serverResponseMs != null ? `${serverResponseMs} ms` : '—'}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Website Visits Today */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="p-4 sm:p-5 rounded-xl bg-gradient-to-br from-brand-600/10 to-blue-600/10 border border-brand-500/20"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-brand-400" />
                      <span className="text-white font-semibold">Besucher Heute</span>
                    </div>
                    <span className="text-brand-400 text-lg font-semibold">
                      {visitorsToday != null ? visitorsToday : '—'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <span>Aufrufe heute (öffentliche Seiten)</span>
                  </div>
                </motion.div>

                {/* Page Views This Week */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="p-4 sm:p-5 rounded-xl bg-gradient-to-br from-brand-600/10 to-brand-700/10 border border-brand-500/20"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-brand-400" />
                      <span className="text-white font-semibold">Seitenaufrufe (7 Tage)</span>
                    </div>
                    <span className="text-brand-400 text-lg font-semibold">
                      {pageViews7d != null ? pageViews7d : '—'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-brand-500 to-brand-400 rounded-full transition-all duration-500" 
                        style={{ width: pageViews7d != null && pageViews7d > 0 ? `${Math.min(100, (pageViews7d / 200) * 100)}%` : '0%' }} 
                      />
                    </div>
                    <span className="text-sm text-gray-300 min-w-[2rem]">
                      {pageViews7d != null ? pageViews7d : '—'}
                    </span>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Access */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="relative group min-w-0"
        >
          <div className="relative bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/10 p-5 sm:p-6 md:p-8 overflow-hidden transition-all duration-500 hover:shadow-2xl">
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-brand-500/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="h-8 sm:h-10 w-1 bg-gradient-to-b from-brand-500 to-brand-500 rounded-full shrink-0" />
                <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-light text-white">
                  Schnellzugriff
                </h2>
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                {[
                  { to: '/dashboard/events', icon: Calendar, label: 'Neues Event erstellen' },
                  { to: '/dashboard/brunches', icon: Coffee, label: 'Brunch verwalten' },
                  { to: '/dashboard/users', icon: Users, label: 'Benutzer verwalten' }
                ].map((item, idx) => {
                  const Icon = item.icon
                  return (
                    <motion.div
                      key={item.to}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 + idx * 0.1 }}
                      whileHover={{ x: 4, scale: 1.02 }}
                    >
                      <Link 
                        to={item.to} 
                        className="flex items-center gap-3 sm:gap-4 px-4 py-3.5 sm:px-5 sm:py-4 rounded-xl bg-white/5 hover:bg-gradient-to-r hover:from-brand-600/20 hover:to-brand-500/10 border border-white/5 hover:border-brand-500/30 text-white hover:text-white transition-all duration-300 group/link min-h-[44px] touch-manipulation"
                      >
                        <Icon className="w-5 h-5 text-brand-400" />
                        <span className="flex-1 font-medium">{item.label}</span>
                        <svg className="w-5 h-5 text-gray-400 group-hover/link:text-brand-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
















